'use client';

import { useMemo, useState, type ComponentPropsWithoutRef, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Icon, OptionChip, Swatch, SwatchIndicator, Typography, type TypographyProps } from '@/shared-ui/atoms';
import { Select } from '@/shared-ui/atoms/select';
import { ExpandIcon } from '@/shared-ui/icons';
import { Separator } from '@/components/ui/separator';
import translations from '@/locales/en-GB/translations.json';

export interface AttributeShadeOption {
    id: string;
    name: string;
    color: string;
    availability?: 'in-stock' | 'out-of-stock';
    property?: 'matte' | 'shimmer';
    availabilityMessage?: string;
}

type RequireKey<T, K extends keyof T> = Omit<T, K> & Required<Pick<T, K>>;
type StockShadeOption = RequireKey<AttributeShadeOption, 'availability'>;
type PropertyShadeOption = RequireKey<AttributeShadeOption, 'property'>;

export type AttributeSelectorRootProps = ComponentPropsWithoutRef<'section'>;

const Root = ({ className, children, ...props }: AttributeSelectorRootProps) => {
    return (
        <section className={cn('space-y-4', className)} data-slot="attribute-selector" {...props}>
            {children}
        </section>
    );
};

Root.displayName = 'AttributeSelector.Root';

export type AttributeSelectorHeadingProps = Omit<TypographyProps, 'variant' | 'weight'>;

const Heading = ({ className, children, ...props }: AttributeSelectorHeadingProps) => {
    return (
        <Typography className={className} variant="h4" weight="regular" {...props}>
            {children}
        </Typography>
    );
};

Heading.displayName = 'AttributeSelector.Heading';

export type AttributeSelectorSizeValues = {
    id: string;
    label: string;
    isOutOfStock?: boolean;
    discontinued?: boolean;
    availabilityMessage?: string;
};

export type AttributeSelectorSizeProps = ComponentPropsWithoutRef<'div'> & {
    sizes?: Array<AttributeSelectorSizeValues>;
    value?: string;
    defaultValue?: string;
    onValueChange?: (value: string) => void;
    children?: ReactNode;
};

const Size = ({
    className,
    sizes,
    value,
    defaultValue,
    onValueChange,
    children,
    ...props
}: AttributeSelectorSizeProps) => {
    const initialSizeId = defaultValue ?? sizes?.[0]?.id;
    const [internalValue, setInternalValue] = useState<string | undefined>(initialSizeId);
    const selectedSizeId = value ?? internalValue;
    const selectedSize = sizes?.find((size) => size.id === selectedSizeId);

    const handleValueChange = (newValue: string) => {
        if (value === undefined) {
            setInternalValue(newValue);
        }

        onValueChange?.(newValue);
    };

    return (
        <div className={cn('flex flex-wrap gap-4 flex-col', className)} data-slot="attribute-selector-size" {...props}>
            <div className="flex gap-2">
                {sizes
                    ? sizes.map((size) => (
                          <OptionChip
                              key={size.id}
                              discontinued={size.discontinued}
                              isOutOfStock={size.isOutOfStock}
                              isSelected={selectedSizeId === size.id}
                              onClick={() => handleValueChange(size.id)}>
                              {size.label}
                          </OptionChip>
                      ))
                    : children}
            </div>
            {selectedSize?.availabilityMessage && (
                <Typography className="block text-sm font-light text-primary-hover" variant="p">
                    {selectedSize.availabilityMessage}
                </Typography>
            )}
        </div>
    );
};

Size.displayName = 'AttributeSelector.Size';

type AttributeSelectorShadeCommonProps = ComponentPropsWithoutRef<'div'> & {
    value?: string;
    defaultValue?: string;
    onValueChange?: (value: string) => void;
    triggerLabel?: string;
};

export type AttributeSelectorShadeProps =
    | (AttributeSelectorShadeCommonProps & {
          variant?: 'stock';
          shades: StockShadeOption[];
      })
    | (AttributeSelectorShadeCommonProps & {
          variant: 'shade';
          shades: PropertyShadeOption[];
      });

type ShadeVariant = NonNullable<AttributeSelectorShadeProps['variant']>;
type ShadeGroupValue =
    | NonNullable<AttributeShadeOption['availability']>
    | NonNullable<AttributeShadeOption['property']>;

const SHADE_GROUP_CONFIG: Record<
    ShadeVariant,
    {
        key: 'availability' | 'property';
        group1Value: ShadeGroupValue;
        group2Value: ShadeGroupValue;
        group1Label: string;
        group2Label: string;
        group1ClassName: string;
        group2ClassName: string;
    }
> = {
    stock: {
        key: 'availability',
        group1Value: 'in-stock',
        group2Value: 'out-of-stock',
        group1Label: translations.pdp.attributeSelector.inStockShades,
        group2Label: translations.pdp.attributeSelector.outOfStockShades,
        group1ClassName: 'text-snk-status-success-dark',
        group2ClassName: 'text-destructive',
    },
    shade: {
        key: 'property',
        group1Value: 'matte',
        group2Value: 'shimmer',
        group1Label: translations.pdp.attributeSelector.matteShades,
        group2Label: translations.pdp.attributeSelector.shimmerShades,
        group1ClassName: 'text-foreground',
        group2ClassName: 'text-foreground',
    },
};

type ShadeSelectOptionProps = {
    shade: AttributeShadeOption;
};

type ShadeSelectSwatchProps = {
    shade: AttributeShadeOption;
    isSelected: boolean;
    onSelect: (shadeId: string) => void;
};

const ShadeSelectSwatch = ({ shade, isSelected, onSelect }: ShadeSelectSwatchProps) => {
    return (
        <Swatch
            size="sm"
            aria-label={shade.name}
            checked={isSelected}
            colour={shade.color}
            status={shade.availability === 'out-of-stock' ? 'unavailable' : 'default'}
            className={cn('size-6 rounded-full outline-offset-2')}
            onCheckedChange={() => onSelect(shade.id)}
            hasTooltip
            tooltipText={`Shade ${shade.name}`}
        />
    );
};

type ShadeSwatchListProps = {
    shades: AttributeShadeOption[];
    selectedShadeId?: string;
    onSelect: (shadeId: string) => void;
    className?: string;
    ariaLabel: string;
};

const ShadeSwatchList = ({ shades, selectedShadeId, onSelect, className, ariaLabel }: ShadeSwatchListProps) => {
    return (
        <ul className={cn('flex flex-wrap gap-2', className)} aria-label={ariaLabel}>
            {shades.map((shade) => (
                <li key={shade.id}>
                    <ShadeSelectSwatch shade={shade} isSelected={shade.id === selectedShadeId} onSelect={onSelect} />
                </li>
            ))}
        </ul>
    );
};

const ShadeSelectOption = ({ shade }: ShadeSelectOptionProps) => {
    return (
        <Select.Option value={shade.id} textValue={shade.name} className="!h-auto flex-col items-start gap-1 px-4 py-2">
            <div className="flex items-center gap-3 w-full">
                <SwatchIndicator
                    aria-hidden="true"
                    className="size-6 shrink-0"
                    colour={shade.color}
                    status={shade.availability === 'out-of-stock' ? 'unavailable' : 'default'}
                />
                <Typography className="font-normal text-base tracking-[1.44px] uppercase" variant="h5">
                    {shade.name}
                </Typography>
            </div>

            {shade.availabilityMessage && (
                <Typography className="block text-sm font-light text-primary-hover" variant="p">
                    {shade.availabilityMessage}
                </Typography>
            )}
        </Select.Option>
    );
};

const Shade = ({
    shades,
    value,
    defaultValue,
    onValueChange,
    triggerLabel = 'Select shade',
    variant = 'stock',
    className,
    ...props
}: AttributeSelectorShadeProps) => {
    const groupConfig = SHADE_GROUP_CONFIG[variant];
    const group1 = shades.filter((shade) => shade.color && shade[groupConfig.key] === groupConfig.group1Value);
    const group2 = shades.filter((shade) => shade.color && shade[groupConfig.key] === groupConfig.group2Value);
    const initialShadeId = defaultValue ?? shades[0]?.id;
    const [internalValue, setInternalValue] = useState<string | undefined>(initialShadeId);
    const selectedShadeId = value ?? internalValue;

    const selectedShade = useMemo(
        () => shades.find((shade) => shade.id === selectedShadeId) ?? shades[0],
        [shades, selectedShadeId]
    );

    const handleValueChange = (newValue: string) => {
        if (value === undefined) {
            setInternalValue(newValue);
        }

        onValueChange?.(newValue);
    };

    return (
        <div
            data-variant={variant}
            className={cn('group space-y-4', className)}
            data-slot="attribute-selector-shade"
            {...props}>
            <Select.Root placeholder={triggerLabel} value={selectedShadeId} onValueChange={handleValueChange}>
                <Select.Trigger aria-label={triggerLabel}>
                    <span className="mr-3 inline-flex items-center">
                        <SwatchIndicator
                            aria-hidden="true"
                            className="size-[18px] shrink-0"
                            colour={selectedShade?.color}
                            status={selectedShade?.availability === 'out-of-stock' ? 'unavailable' : 'default'}
                        />
                    </span>
                    <span className="min-w-0 flex-1 truncate font-normal text-base tracking-[1.44px] uppercase">
                        {selectedShade?.name ?? triggerLabel}
                    </span>
                    <Select.TriggerIcon>
                        <Icon className="size-3" icon={ExpandIcon} decorative />
                    </Select.TriggerIcon>
                </Select.Trigger>
                <Select.Content className="px-0 py-0 max-h-[430px] overflow-y-auto">
                    {group1.length > 0 && (
                        <Typography
                            className={cn('px-4 py-2 !tracking-[0.16px]', groupConfig.group1ClassName)}
                            variant="p">
                            {groupConfig.group1Label}
                        </Typography>
                    )}

                    {group1.map((shade) => (
                        <ShadeSelectOption key={shade.id} shade={shade} />
                    ))}

                    {group2.length > 0 && (
                        <>
                            <Separator className="h-[1px] bg-muted-foreground" />
                            <Typography
                                className={cn('px-4 pb-2 pt-4 !tracking-[0.16px]', groupConfig.group2ClassName)}
                                variant="p">
                                {groupConfig.group2Label}
                            </Typography>
                        </>
                    )}

                    {group2.map((shade) => (
                        <ShadeSelectOption key={shade.id} shade={shade} />
                    ))}
                </Select.Content>
            </Select.Root>

            {group1.length > 0 && (
                <ShadeSwatchList
                    shades={group1}
                    selectedShadeId={selectedShade?.id}
                    onSelect={handleValueChange}
                    className="mt-3 mx-1 mb-4"
                    ariaLabel="Shade options"
                />
            )}

            {group2.length > 0 && (
                <Typography className="mt-4 mb-3" variant="p">
                    {groupConfig.group2Label}
                </Typography>
            )}

            {group2.length > 0 && (
                <ShadeSwatchList
                    shades={group2}
                    selectedShadeId={selectedShade?.id}
                    onSelect={handleValueChange}
                    className="mx-1"
                    ariaLabel={`${groupConfig.group2Label} options`}
                />
            )}
        </div>
    );
};

Shade.displayName = 'AttributeSelector.Shade';

export const AttributeSelector = {
    Root,
    Heading,
    Size,
    Shade,
} as const;
